import * as rudderanalytics from 'rudder-sdk-js';
import type { App } from 'vue';

import {
  BusinessVariant,
  Freelance,
  FreelanceMissionProposalAnswerEnum,
  MarketSituationInput,
  SelectionStatus,
} from '@freelancerepublik/graphql-types';
import { CallToActionUseCase } from '@/domain/candidate/models/MarketSituation.ui';

import { ENV } from '@/env';

function trackUserSignup(flow: 'first_use' | 'invitation') {
  rudderanalytics.track('User signed up', { flow });
}

function trackUserCreated(user: Record<string, any>) {
  rudderanalytics.track('User Created', { user });
}

function trackUserUpdated({
  firstUse,
  user,
}: {
  firstUse?: {
    is_completed?: boolean;
    step: number;
    step_name: string;
    completion_rate: number;
    variant: BusinessVariant;
  };
  user: Record<string, any>;
}) {
  rudderanalytics.track('User Updated', { first_use: firstUse, user });
}

function trackMissionAssumed({
  firstUse,
  mission,
}: {
  firstUse?: {
    is_completed?: boolean;
    step: number;
    step_name: string;
    completion_rate: number;
    variant: BusinessVariant;
  };
  mission: Record<string, any>;
}) {
  rudderanalytics.track('Mission Assumed', { first_use: firstUse, mission });
}

function trackUserLogin() {
  rudderanalytics.track('User logged in');
}

function trackFreelanceMissionInterest({
  missionId,
  freelanceId,
  answer,
  source,
}: {
  missionId: string;
  freelanceId: string;
  answer: FreelanceMissionProposalAnswerEnum;
  source: 'proposalsPipeline' | 'emailAnswer';
}) {
  rudderanalytics.track('Freelance answers proposal', { missionId, freelanceId, answer, source });
}

function trackFreelanceMarketSituationEdit(ms: MarketSituationInput, uc: CallToActionUseCase) {
  rudderanalytics.track('Freelance updates situation', { marketSituation: ms, useCase: uc });
}

function trackInsiderNotifyingFreelance({
  action,
  shouldNotBeNotified,
  status,
  freelanceId,
  weightedScore,
  missionId,
}: {
  action: string;
  shouldNotBeNotified: boolean;
  status: SelectionStatus;
  freelanceId: string;
  weightedScore: number;
  missionId: string;
}) {
  rudderanalytics.track('Insider notifies freelance', {
    action, shouldNotBeNotified, status, freelanceId, weightedScore, missionId,
  });
}

function trackInsiderClickingFreelanceDetailsTab(tabLabel: string) {
  rudderanalytics.track('Insider clicks freelance details page', { 'Tab clicked': tabLabel });
}

type PreselectionData = {
  mission: { _id: string; title: string; profile: string };
  selections: { _id: string; fullname: string }[];
};

function trackInsiderCancelingPreselection(data: PreselectionData) {
  rudderanalytics.track('Insider cancels preselection basket', data);
}

function trackInsiderAddFreelanceToPreselection(selectAndNotify: boolean, data: PreselectionData) {
  const eventName = selectAndNotify
    ? 'Insider validates and notify a preselection basket'
    : 'Insider validates a preselection basket';
  rudderanalytics.track(eventName, data);
}

function trackEditKeySkillsProposal({
  missionId,
  freelanceId,
  source,
}: {
  missionId: string;
  freelanceId: string;
  source: 'app' | 'admin';
}) {
  if (source === 'admin') {
    rudderanalytics.track('Insider edits freelance key skills proposal', { missionId, freelanceId });
  } else {
    rudderanalytics.track('Freelance edits key skills proposal', { missionId, freelanceId });
  }
}

type SelectionButtonData = {
  freelance: Freelance;
  missionId: string;
  sort: string;
  refinements: any;
  presets: string[];
};

function trackSelectionButtonClick(data: SelectionButtonData) {
  // @ts-expect-error
  rudderanalytics.track('Insider clicks selection button', data);
}

function trackUIClicked(data: any) {
  rudderanalytics.track('UI Clicked', data);
}

const rudderstackFunctions = {
  trackUserLogin,
  trackUserSignup,
  trackUserCreated,
  trackUserUpdated,
  trackMissionAssumed,
  trackFreelanceMissionInterest,
  trackFreelanceMarketSituationEdit,
  trackInsiderNotifyingFreelance,
  trackInsiderClickingFreelanceDetailsTab,
  trackInsiderCancelingPreselection,
  trackInsiderAddFreelanceToPreselection,
  trackEditKeySkillsProposal,
  trackSelectionButtonClick,
  trackUIClicked,
};

export type RudderstackType = typeof rudderanalytics & typeof rudderstackFunctions;

// Dummy object to avoid errors when rudderstack is not initialized
const dummyRudderstack = new Proxy(
  {},
  {
    get:
      (_target, propKey) =>
      (...args: any[]) => {
        console.log(`rudderstack.${String(propKey)}`, ...args);
      },
  }
);

import { rudderstackKey } from '@/infra/injection-keys';
export { rudderstackKey };

export const rudderstackPlugin = {
  install(app: App) {
    let ra: RudderstackType;

    if (ENV.RUDDERSTACK_WRITE_KEY && ENV.RUDDERSTACK_DATA_PLANE) {
      rudderanalytics.load(ENV.RUDDERSTACK_WRITE_KEY, ENV.RUDDERSTACK_DATA_PLANE);
      ra = { ...rudderanalytics, ...rudderstackFunctions } as RudderstackType;
    } else {
      ra = dummyRudderstack as unknown as RudderstackType;
    }

    app.provide(rudderstackKey, ra);
  },
};
